/* eslint-disable consistent-return */
import React, { useCallback, useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Container from '../../common/components/Container/Container';
import Loading from '../../common/components/Loading/Loading';
import MessagePanel from '../../common/components/MessagePanel/MessagePanel';
import { Provider } from '../../config';
import { Routes } from '../../config/routes';
import { ValidationContext } from '../../providers/ValidationContext';

import { CredentialShareContext, ShareStatus } from '../../providers/CredentialShareContext';
import { FlowConfigContext } from '../../providers/FlowConfigContext';
import { ScopeRequestContext } from '../../providers/ScopeRequestContext';
import withRouteAnalytics from '../../services/analytics/withRouteAnalytics';
import { Screens } from '../../services/analytics/screens';
import { CredentialStoreContext } from '../../providers/CredentialStoreContext';
import { ScopeRequestStatus } from '../../services/secureIdentityProvider';

function ValidationFinalizingScreen(): JSX.Element {
  const currentLocation = useLocation();
  const { shareStatus, handleCredentialsShare } = useContext(CredentialShareContext);
  const { chosenCredentialIds } = useContext(CredentialStoreContext);
  const { getEventPayload, deleteVerification, shouldInitiateCredentialStorage, validationId } =
    useContext(ValidationContext);
  const { initialized, flowSuccessful, flowFailed } = useContext(FlowConfigContext);
  const scopeRequestContext = useContext(ScopeRequestContext);
  const navigate = useNavigate();

  const emitFlowResult = useCallback(
    async (abortController: AbortController) => {
      const eventPayload = await getEventPayload();
      if (abortController.signal.aborted) {
        return;
      }
      if (eventPayload.isFailed()) {
        flowFailed({ provider: Provider.WEB });
        return;
      }

      flowSuccessful(eventPayload.success());
    },
    [flowFailed, flowSuccessful, getEventPayload],
  );

  const handleCredentialsSharing = useCallback(
    async (abortController: AbortController) => {
      const shareResult = await handleCredentialsShare(abortController);
      if (abortController.signal.aborted || shareResult === undefined) return;
      if (shareResult.isFailed()) {
        shareResult.logError('unexpected error finalizing flow');
        navigate(Routes.VALIDATION_ERROR);
      }
    },
    [handleCredentialsShare, navigate],
  );

  const handleShareStatusFailed = useCallback(async () => {
    const shareResult = await deleteVerification(false, false);
    if (shareResult.isFailed()) {
      shareResult.logError('unexpected error deleting verification');
    }
    navigate(Routes.VALIDATION_REJECTED);
  }, [deleteVerification, navigate]);

  useEffect(() => {
    const abortController = new AbortController();
    if (!initialized) {
      return;
    }
    // check if we should store credentials before proceeding, in case the user exited a previous flow before storing
    shouldInitiateCredentialStorage(validationId).then((shouldStore: boolean) => {
      // if (abortController.signal.aborted) return;
      // stop us going into an infinite loop
      if (shouldStore && ![Routes.STORE_CREDENTIALS].includes(currentLocation?.state?.prevPath)) {
        navigate(Routes.STORE_CREDENTIALS, { state: { prevPath: Routes.VALIDATION_FINALIZING } });
        return null;
      }

      if (
        shareStatus === ShareStatus.pending ||
        (chosenCredentialIds.length >= 1 && // The user has opted to share credentials
          scopeRequestContext.scopeRequest?.status === ScopeRequestStatus.AWAITING_USER) // And we haven't resolved the SR yet
      ) {
        handleCredentialsSharing(abortController);
      }
    });

    return () => abortController.abort();
  }, [
    currentLocation?.state?.prevPath,
    handleCredentialsSharing,
    initialized,
    navigate,
    shouldInitiateCredentialStorage,
    shareStatus,
    chosenCredentialIds,
    scopeRequestContext.scopeRequest?.status,
    validationId,
  ]);

  useEffect(() => {
    const abortController = new AbortController();
    if (!initialized) {
      return;
    }

    if (shareStatus === ShareStatus.shared) {
      emitFlowResult(abortController);
    }
    if (shareStatus === ShareStatus.failed) {
      handleShareStatusFailed();
    }
    return () => {
      abortController.abort();
    };
  }, [emitFlowResult, handleShareStatusFailed, initialized, shareStatus]);

  return (
    <>
      {!scopeRequestContext.scopeRequest && (
        <Container>
          <Loading data-testid="DOCUMENT_SUCCESS" />
        </Container>
      )}
      {scopeRequestContext.scopeRequest && (
        <MessagePanel
          title="screens.documentPass.finalizing.title"
          description="screens.documentPass.finalizing.description"
          dataTestId="DOCUMENT_FINALIZING"
          showUserInfo
          showSupportLink
        />
      )}
    </>
  );
}
export default withRouteAnalytics(ValidationFinalizingScreen, Screens.DOCUMENT_FINALIZING);
